import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import P2PMarketplaceService from '../../services/p2pMarketplace.service';
import UserPaymentMethodsService from '../../services/userPaymentMethods.service';
import { setFilters } from '../../request/P2P/p2pSlice';
import UserPaymentMethodsModal from './UserPaymentMethodsModal';
// import LoadCurrencies from '../../request/Countries/LoadCurrencies';
import { 
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  useMediaQuery,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Skeleton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  
} from '@mui/material';
import {
  Search,
  TrendingUp,
  TrendingDown,
  Star,
  StarBorder,
  FilterList,
  History,
  Verified,
  LocationOn,
  Payment,
  AccessTime,
  AccountBalanceWallet,
  ArrowForward,
  ExpandMore,
  CheckCircle,
  Warning,
  Info,
  Chat,
  Phone,
  Email,
  AccountBalance,
  Security,
  Speed,
  SupportAgent,
  Close,
  Refresh,
  Timer,
  AttachMoney,
  TrendingFlat,
  Add,
  Remove
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const P2PSellInterface = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  
  // Redux state
  const fundingBal = useSelector((state) => state.wallet.fundingBalance);
  const coinsData = useSelector((state) => state.coins.coinRates) || [];
  const p2pOffers = useSelector((state) => state.p2p?.offers || []);
  const p2pLoading = useSelector((state) => state.p2p?.loading || false);
  const filters = useSelector((state) => state.p2p?.filters || {});
  
  // Get user data from unified system
  const userData = useSelector((state) => state.userData?.user);
  const userLoading = useSelector((state) => state.userData?.loading || false);

  // Read the same sources used by /assets for coin balance calculation
  const walletFundings = useSelector((state) => state.wallet.fundings) || [];
  const historyWithdrawals = useSelector((state) => state.history.withdrawals) || [];
  const historyTradings = useSelector((state) => state.history.tradings) || [];
  const historyInvestments = useSelector((state) => state.history.investments) || [];
  const coinRates = useSelector((state) => state.coins.coinRates) || [];
  
  // Load currencies from Redux store
  const currencies = useSelector((state) => state.countries.currencies) || [];

  // State
  const [selectedCrypto, setSelectedCrypto] = useState(filters.cryptocurrency || 'USDT');
  const [selectedFiat, setSelectedFiat] = useState(filters.fiatCurrency || 'NGN');
  const [searchAmount, setSearchAmount] = useState(filters.amount || '');
  const [paymentMethod, setPaymentMethod] = useState(filters.paymentMethod || '');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderStep, setOrderStep] = useState(0);
  const [orderAmount, setOrderAmount] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [userPaymentMethods, setUserPaymentMethods] = useState([]);
  const [userPaymentDetails, setUserPaymentDetails] = useState({});
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [amountType, setAmountType] = useState('crypto'); // 'crypto' or 'fiat'
  const [fiatAmount, setFiatAmount] = useState('');

  const availableBalance = (symbol) => getCoinBalance(symbol);
  const getBalanceRounded8 = (symbol) => {
    const bal = getCoinBalance(symbol);
    return Math.floor(bal * 1e8) / 1e8; // never exceed actual balance after rounding
  };
  const clampAmount = (val) => {
    const num = Number(val || 0);
    const max = getBalanceRounded8(selectedCrypto);
    if (Number.isNaN(num)) return '';
    if (num < 0) return '0';
    return num > max ? String(max.toFixed(8)) : String(num);
  };

  // Conversion functions
  const convertFiatToCrypto = (fiatAmount, rate) => {
    if (!fiatAmount || !rate) return '';
    return (Number(fiatAmount) / Number(rate)).toFixed(8);
  };

  const convertCryptoToFiat = (cryptoAmount, rate) => {
    if (!cryptoAmount || !rate) return '';
    return (Number(cryptoAmount) * Number(rate)).toFixed(2);
  };

  // Get current rate from selected offer
  const getCurrentRate = () => {
    return selectedOffer?.rate || 0;
  };

  // Handle amount changes based on type
  const handleCryptoAmountChange = (value) => {
    setOrderAmount(clampAmount(value));
    if (amountType === 'crypto') {
      const converted = convertCryptoToFiat(value, getCurrentRate());
      setFiatAmount(converted);
    }
  };

  const handleFiatAmountChange = (value) => {
    setFiatAmount(value);
    if (amountType === 'fiat') {
      const converted = convertFiatToCrypto(value, getCurrentRate());
      setOrderAmount(clampAmount(converted));
    }
  };

  const handleAmountTypeChange = (newType) => {
    setAmountType(newType);
    if (newType === 'fiat') {
      // When switching to fiat, use the current crypto amount to calculate fiat
      const converted = convertCryptoToFiat(orderAmount, getCurrentRate());
      setFiatAmount(converted);
    } else {
      // When switching to crypto, use the current fiat amount to calculate crypto
      const converted = convertFiatToCrypto(fiatAmount, getCurrentRate());
      setOrderAmount(clampAmount(converted));
    }
  };

  const isContinueDisabled = (amount, symbol) => {
    const amt = Number(amount);
    const bal8 = getBalanceRounded8(symbol);
    if (!amount || Number.isNaN(amt)) return true;
    if (amt <= 0) return true;
    if (amt - bal8 > 1e-9) return true; // Exceeds available balance
    
    // Check against offer limits (convert fiat limits to crypto)
    if (selectedOffer) {
      const minCrypto = calculateCryptoAmount(selectedOffer.min_amount, selectedOffer.rate);
      const maxCrypto = calculateCryptoAmount(selectedOffer.max_amount, selectedOffer.rate);
      
      if (amt < parseFloat(minCrypto)) return true; // Below minimum
      if (amt > parseFloat(maxCrypto)) return true; // Above maximum
    }
    
    return false;
  };

  const isFiatAmountValid = (fiatAmt) => {
    const amt = Number(fiatAmt);
    if (!fiatAmt || Number.isNaN(amt)) return false;
    if (amt <= 0) return false;
    
    // Check against offer limits
    if (selectedOffer) {
      if (amt < selectedOffer.min_amount) return false; // Below minimum
      if (amt > selectedOffer.max_amount) return false; // Above maximum
    }
    
    // Check if resulting crypto amount exceeds balance
    const cryptoAmt = convertFiatToCrypto(fiatAmt, getCurrentRate());
    const bal8 = getBalanceRounded8(selectedCrypto);
    if (Number(cryptoAmt) > bal8) return false;
    
    return true;
  };

  // Use data from Redux store
  const offers = p2pOffers.filter(offer => 
    offer.type === 'buy' && // For sell interface, show buy offers
    offer.cryptocurrency === selectedCrypto &&
    offer.fiat_currency === selectedFiat &&
    (!paymentMethod || offer.payment_methods.includes(paymentMethod)) &&
    (!searchAmount || (offer.min_amount <= parseFloat(searchAmount) && offer.max_amount >= parseFloat(searchAmount)))
  );
  const loading = p2pLoading;

  // Load user payment methods on component mount
  useEffect(() => {
    loadUserPaymentMethods();
  }, []);

  // Reload payment methods when the modal is opened
  useEffect(() => {
    if (showPaymentMethods) {
      loadUserPaymentMethods();
    }
  }, [showPaymentMethods]);

  // Update Redux filters when local state changes
  useEffect(() => {
    dispatch(setFilters({
      // For SELL, API needs type=sell
      tradeType: 'sell',
      cryptocurrency: selectedCrypto,
      fiatCurrency: selectedFiat,
      paymentMethod: paymentMethod || undefined,
      amount: searchAmount || undefined,
    }));
  }, [selectedCrypto, selectedFiat, searchAmount, paymentMethod, dispatch]);

  // Sync amounts when selected offer changes
  useEffect(() => {
    if (selectedOffer && orderAmount) {
      const converted = convertCryptoToFiat(orderAmount, selectedOffer.rate);
      setFiatAmount(converted);
    }
  }, [selectedOffer]);

  const loadUserPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await UserPaymentMethodsService.getPaymentMethods();
      if (response.status === 'success') {
        // Ensure payment_methods is always an array
        const paymentMethods = Array.isArray(response.payment_methods) ? response.payment_methods : [];
        setUserPaymentMethods(paymentMethods);
        setUserPaymentDetails(response.payment_details || {});
        return response.payment_details || {};
      }
      return {};
    } catch (error) {
      console.error('Error loading user payment methods:', error);
      // Set empty arrays on error to prevent map errors
      setUserPaymentMethods([]);
      setUserPaymentDetails({});
      return {};
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const handlePaymentMethodsSaved = (user) => {
    // Ensure payment_methods is always an array
    const paymentMethods = Array.isArray(user.payment_methods) ? user.payment_methods : [];
    setUserPaymentMethods(paymentMethods);
    setUserPaymentDetails(user.payment_details || {});
    
    // Debug logging to verify the update
    console.log('Payment methods updated successfully:', user.payment_methods);
    console.log('Payment details updated successfully:', user.payment_details);
  };

  const getUsdBalanceForCoin = (symbol) => {
    // Mirror /assets calculation (CryptoAssetList.calculateBalance) → USD amount per coin gateway
    let amount = 0;
    try {
      walletFundings.forEach(fund => {
        if (fund.gateway === symbol) {
          amount += parseFloat(fund.amount || 0);
        }
      });
      historyTradings.forEach(trade => {
        if (trade.pair === symbol) {
          amount += parseFloat(trade.profit || 0);
        }
      });
      historyInvestments.forEach(investment => {
        if (investment.payment_source === symbol && investment.status !== 'declined') {
          amount -= parseFloat(investment.amount_invested || 0);
        }
      });
      historyWithdrawals.forEach(withdrawal => {
        if (withdrawal.source === symbol && withdrawal.status !== 'declined') {
          amount -= parseFloat(withdrawal.amount || 0);
        }
      });
    } catch {}
    return Math.max(0, amount);
  };

  const getPriceUsd = (symbol) => {
    const coin = coinRates.find(c => c.symbol === symbol);
    return coin ? parseFloat(coin.priceUsd || 0) : 0;
  };

  const getCoinBalance = (symbol) => {
    const usd = getUsdBalanceForCoin(symbol);
    const price = getPriceUsd(symbol);
    if (!price || price <= 0) return 0;
    return usd / price;
  };

  const handleCreateOrder = async (offer) => {
    const coinBal = getCoinBalance(selectedCrypto);
    if (coinBal <= 0) {
      alert(`Insufficient ${selectedCrypto} balance.`);
      return;
    }

    setSelectedOffer(offer);
    setOrderAmount(getBalanceRounded8(selectedCrypto).toFixed(8));
    setShowOrderDialog(true);
    setOrderStep(0);
  };

  const handleConfirmOrder = async () => {
    if (!selectedOffer || !orderAmount || !agreeToTerms) {
      alert('Please fill in all required fields and agree to terms.');
      return;
    }

    setCreating(true);
    try {
      // Refresh payment methods to get the latest data before creating order
      const freshPaymentDetails = await loadUserPaymentMethods();
      
      // Convert crypto amount to fiat amount for the API
      const fiatAmount = calculateReceiveAmount(orderAmount, selectedOffer.rate);
      
      // Get payment details for the selected payment method
      const selectedPaymentMethod = selectedOffer.payment_methods[0] || 'Bank Transfer';
      const paymentDetailsForMethod = freshPaymentDetails[selectedPaymentMethod] || {};

      // Debug logging
      console.log('Selected payment method:', selectedPaymentMethod);
      console.log('Fresh payment details:', freshPaymentDetails);
      console.log('Payment details for method:', paymentDetailsForMethod);
      
      const orderData = {
        rate_id: selectedOffer.id,
        amount: parseFloat(fiatAmount), // Send fiat amount to API
        payment_method: selectedPaymentMethod,
        payment_details: paymentDetailsForMethod,
        type: 'sell',
        notes: orderNotes
      };

      const response = await P2PMarketplaceService.createOrder(orderData);
      
      if (response.success) {
        setShowOrderDialog(false);
        navigate(`/p2p/transaction/${response.order.id}`);
      } else {
        alert(response.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to create order. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const formatPrice = (amount, currency = 'NGN') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatCryptoAmount = (amount, crypto) => {
    return `${parseFloat(amount).toFixed(8)} ${crypto}`;
  };

  const calculateReceiveAmount = (cryptoAmount, rate) => {
    if (!cryptoAmount || !rate) return '0.00';
    // For sell orders: crypto amount * rate = fiat amount received
    return (parseFloat(cryptoAmount) * parseFloat(rate)).toFixed(2);
  };

  const calculateCryptoAmount = (fiatAmount, rate) => {
    if (!fiatAmount || !rate) return '0.00000000';
    // For sell orders: fiat amount / rate = crypto amount needed
    return (parseFloat(fiatAmount) / parseFloat(rate)).toFixed(8);
  };

  const renderOfferCard = (offer, index) => (
    <motion.div
      key={offer.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card 
        sx={{ 
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 8,
            transform: 'translateY(-4px)',
            borderColor: theme.palette.error.main
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Partner Info */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    offer.partner.is_verified ? (
                      <Verified sx={{ fontSize: 16, color: 'primary.main' }} />
                    ) : null
                  }
                >
                  <Avatar
                    sx={{ 
                      width: 56, 
                      height: 56,
                      bgcolor: theme.palette.error.main,
                      fontSize: '1.4rem',
                      fontWeight: 600
                    }}
                  >
                    {offer.partner.name.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {offer.partner.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i}
                          sx={{ 
                            fontSize: 16,
                            color: i < Math.floor(offer.partner.rating) ? 'warning.main' : 'grey.300'
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      ({offer.partner.rating.toFixed(1)})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {offer.partner.country || 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Rate & Amount */}
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.error.main,
                    mb: 1
                  }}
                >
                  {formatPrice(offer.rate, offer.fiat_currency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  per {offer.cryptocurrency}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Chip 
                    label={`${offer.partner.total_trades} trades`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${offer.partner.completion_rate}% completion`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Limits & Payment */}
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Available: {formatCryptoAmount(offer.available_balance, offer.cryptocurrency)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Limits: {formatCryptoAmount(calculateCryptoAmount(offer.min_amount, offer.rate), offer.cryptocurrency)} - {formatCryptoAmount(calculateCryptoAmount(offer.max_amount, offer.rate), offer.cryptocurrency)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  ({formatPrice(offer.min_amount, offer.fiat_currency)} - {formatPrice(offer.max_amount, offer.fiat_currency)} {offer.fiat_currency})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Payment sx={{ fontSize: 14, color: 'text.secondary' }} />
                  {offer.payment_methods.slice(0, 2).map((method, idx) => (
                    <Chip 
                      key={idx}
                      label={method}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                  ))}
                  {offer.payment_methods.length > 2 && (
                    <Typography variant="caption" color="text.secondary">
                      +{offer.payment_methods.length - 2} more
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {offer.time_limit} min
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Action Button */}
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  onClick={() => handleCreateOrder(offer)}
                  disabled={creating || offer.available_balance <= 0}
                  endIcon={<ArrowForward />}
                  sx={{ 
                    minWidth: 160,
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2
                  }}
                >
                  Sell {offer.cryptocurrency}
                </Button>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                  <Chat sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Chat available
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );

  const steps = [
    'Select Amount',
    'Payment Method',
    'Confirm Order'
  ];

  return (
    <Box sx={{ p: 3 }}>

      {/* Filters */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Sell {selectedCrypto} for {selectedFiat}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                  <CircularProgress size={18} thickness={5} />
                  <Typography variant="body2" color="text.secondary">Loading</Typography>
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Amount Input */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={searchAmount}
                onChange={(e) => setSearchAmount(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBalanceWallet />
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter amount"
              />
            </Grid>

            {/* Fiat Currency */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={selectedFiat}
                  onChange={(e) => setSelectedFiat(e.target.value)}
                  label="Currency"
                >
                  {currencies.length > 0 ? (
                    currencies.map((currency) => (
                      <MenuItem key={currency.currency_code} value={currency.currency_code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {currency.flag && (
                            <img 
                              src={currency.flag} 
                              alt={currency.country_name}
                              style={{ width: 20, height: 15, objectFit: 'cover', borderRadius: '2px' }}
                            />
                          )}
                          <Typography variant="body2">
                            {currency.currency_code} ({currency.currency})
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    ['NGN', 'USD', 'EUR', 'GBP', 'CAD'].map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        <Typography variant="body2">{currency}</Typography>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Crypto Selection */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Crypto</InputLabel>
                <Select
                  value={selectedCrypto}
                  onChange={(e) => setSelectedCrypto(e.target.value)}
                  label="Crypto"
                >
                  {coinsData && coinsData.length > 0 ? (
                    coinsData.slice(0, 20).map((coin) => (
                      <MenuItem key={coin.symbol} value={coin.symbol}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={`https://assets.coincap.io/assets/icons/${coin.symbol.toLowerCase()}@2x.png`}
                            alt={coin.name}
                            style={{ width: 20, height: 20, objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Typography variant="body2">
                            {coin.symbol} - {coin.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    ['USDT', 'BTC', 'ETH', 'BNB', 'SOL'].map((crypto) => (
                      <MenuItem key={crypto} value={crypto}>
                        <Typography variant="body2">{crypto}</Typography>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Payment</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  label="Payment"
                >
                  <MenuItem value="">All Methods</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Mobile Money">Mobile Money</MenuItem>
                  <MenuItem value="PayPal">PayPal</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons removed: auto-load on selection; keep History only */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button variant="outlined" startIcon={<History />} onClick={() => navigate('/p2p/history')}>
                  History
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          <Collapse in={showFilters}>
            <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Min Rating</InputLabel>
                    <Select value="" label="Min Rating">
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="4">4+ Stars</MenuItem>
                      <MenuItem value="3">3+ Stars</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Completion Rate</InputLabel>
                    <Select value="" label="Completion Rate">
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="95">95%+</MenuItem>
                      <MenuItem value="90">90%+</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Response Time</InputLabel>
                    <Select value="" label="Response Time">
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="15">15 min</MenuItem>
                      <MenuItem value="30">30 min</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={<Switch />}
                    label="Verified Only"
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Results */}
      <Box>
        {loading ? (
          <Box>
            {[...Array(3)].map((_, index) => (
              <Card key={index} sx={{ mb: 2, p: 3 }}>
                <Skeleton variant="rectangular" height={120} />
              </Card>
            ))}
          </Box>
        ) : offers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No offers available for the selected criteria
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or check back later
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {offers.length} Buy Offers Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sorted by highest price
              </Typography>
            </Box>
            
            <AnimatePresence>
              {offers.map((offer, index) => renderOfferCard(offer, index))}
            </AnimatePresence>
          </Box>
        )}
      </Box>

      {/* Order Creation Dialog */}
      <Dialog 
        open={showOrderDialog} 
        onClose={() => setShowOrderDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Create Sell Order
            </Typography>
            <IconButton onClick={() => setShowOrderDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={orderStep} orientation={isMobile ? 'vertical' : 'horizontal'}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                  {index === 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Enter Amount
                      </Typography>
                      
                      {/* Amount Type Toggle */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Paper sx={{ p: 1, display: 'flex', gap: 1 }}>
                          <Button
                            variant={amountType === 'crypto' ? 'contained' : 'outlined'}
                            onClick={() => handleAmountTypeChange('crypto')}
                            size="small"
                            sx={{ minWidth: 80 }}
                          >
                            {selectedCrypto}
                          </Button>
                          <Button
                            variant={amountType === 'fiat' ? 'contained' : 'outlined'}
                            onClick={() => handleAmountTypeChange('fiat')}
                            size="small"
                            sx={{ minWidth: 80 }}
                          >
                            {selectedOffer?.fiat_currency || selectedFiat}
                          </Button>
                        </Paper>
                      </Box>

                      {/* Crypto Amount Input */}
                      <TextField
                        fullWidth
                        label={`Amount to Sell (Available: ${getBalanceRounded8(selectedCrypto).toFixed(8)} ${selectedCrypto})`}
                        type="number"
                        value={orderAmount}
                        onChange={(e) => handleCryptoAmountChange(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {selectedCrypto}
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button size="small" onClick={() => handleCryptoAmountChange(getBalanceRounded8(selectedCrypto).toFixed(8))}>All</Button>
                            </InputAdornment>
                          )
                        }}
                        inputProps={{ step: '0.00000001', min: '0' }}
                        sx={{ mb: 2, display: amountType === 'crypto' ? 'block' : 'none' }}
                      />

                      {/* Fiat Amount Input */}
                      <TextField
                        fullWidth
                        label={`Amount to Receive (${selectedOffer?.fiat_currency || selectedFiat})`}
                        type="number"
                        value={fiatAmount}
                        onChange={(e) => handleFiatAmountChange(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {selectedOffer?.fiat_currency || selectedFiat}
                            </InputAdornment>
                          )
                        }}
                        inputProps={{ step: '0.01', min: '0' }}
                        error={fiatAmount && !isFiatAmountValid(fiatAmount)}
                        helperText={
                          fiatAmount && !isFiatAmountValid(fiatAmount) ? (
                            selectedOffer ? (
                              `Amount must be between ${formatPrice(selectedOffer.min_amount, selectedOffer.fiat_currency)} and ${formatPrice(selectedOffer.max_amount, selectedOffer.fiat_currency)}`
                            ) : 'Invalid amount'
                            ) : (
                              selectedOffer ? `Limits: ${formatPrice(selectedOffer.min_amount, selectedOffer.fiat_currency)} - ${formatPrice(selectedOffer.max_amount, selectedOffer.fiat_currency)}` : ''
                            )
                        }
                        sx={{ mb: 2, display: amountType === 'fiat' ? 'block' : 'none' }}
                      />

                      {/* Conversion Display */}
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>You will receive:</strong> {formatPrice(calculateReceiveAmount(orderAmount, selectedOffer?.rate), selectedOffer?.fiat_currency)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>You will sell:</strong> {orderAmount} {selectedCrypto}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Rate:</strong> {formatPrice(selectedOffer?.rate || 0, selectedOffer?.fiat_currency)} per {selectedCrypto}
                        </Typography>
                      </Alert>
                      
                      {selectedOffer && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="bold">
                            Trading Limits:
                          </Typography>
                          <Typography variant="body2">
                            {formatCryptoAmount(calculateCryptoAmount(selectedOffer.min_amount, selectedOffer.rate), selectedOffer.cryptocurrency)} - {formatCryptoAmount(calculateCryptoAmount(selectedOffer.max_amount, selectedOffer.rate), selectedOffer.cryptocurrency)} {selectedOffer.cryptocurrency}
                          </Typography>
                          <Typography variant="body2">
                          ({formatPrice(selectedOffer.min_amount, selectedOffer.fiat_currency)} - {formatPrice(selectedOffer.max_amount, selectedOffer.fiat_currency)} {selectedOffer.fiat_currency})
                          </Typography>
                        </Alert>
                      )}
                      
                      <Button
                        variant="contained"
                        onClick={() => setOrderStep(1)}
                        disabled={
                          amountType === 'crypto' 
                            ? isContinueDisabled(orderAmount, selectedCrypto)
                            : !isFiatAmountValid(fiatAmount)
                        }
                      >
                        Continue
                      </Button>
                    </Box>
                  )}
                  
                  {index === 1 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Your Payment Methods
                      </Typography>
                      <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Partner: {selectedOffer?.partner.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Rate: {formatPrice(selectedOffer?.rate, selectedOffer?.fiat_currency)} per {selectedOffer?.cryptocurrency}
                        </Typography>
                        
                        {/* User Payment Methods and Details */}
                        {Array.isArray(userPaymentMethods) && userPaymentMethods.length > 0 ? (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                              Your Payment Methods (Where you'll receive payment):
                            </Typography>
                            {userPaymentMethods.map((method) => (
                              <Card key={method} sx={{ mb: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                    {method}
                                  </Typography>
                                  {userPaymentDetails[method] && (
                                    <Box sx={{ mt: 1 }}>
                                      {Object.entries(userPaymentDetails[method]).map(([key, value]) => (
                                        value && (
                                          <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {value}
                                          </Typography>
                                        )
                                      ))}
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            ))}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              The partner will send payment to one of these methods after you transfer your crypto.
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ mt: 2, textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              No payment methods configured. Add your payment details to receive payments.
                            </Typography>
                          </Box>
                        )}
                      </Card>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setShowPaymentMethods(true)}
                        sx={{ mb: 2 }}
                        disabled={loadingPaymentMethods}
                      >
                        {loadingPaymentMethods ? 'Loading...' : 'Manage Payment Methods'}
                      </Button>
                      <TextField
                        fullWidth
                        label="Notes (Optional)"
                        multiline
                        rows={3}
                        value={orderNotes}
                        onChange={(e) => setOrderNotes(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setOrderStep(0)}>
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => setOrderStep(2)}
                          disabled={!Array.isArray(userPaymentMethods) || userPaymentMethods.length === 0}
                        >
                          Continue
                        </Button>
                      </Box>
                    </Box>
                  )}
                  
                  {index === 2 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Confirm Order
                      </Typography>
                      <Card sx={{ p: 2, mb: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Amount to Sell
                            </Typography>
                            <Typography variant="h6">
                              {orderAmount} {selectedOffer?.cryptocurrency}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              You Will Receive
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {formatPrice(calculateReceiveAmount(orderAmount, selectedOffer?.rate), selectedOffer?.fiat_currency)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Card>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Please wait for the counterparty to make payment. The tokens for this sale will be transferred out of your Funding Account.
                      </Alert>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={agreeToTerms}
                            onChange={(e) => setAgreeToTerms(e.target.checked)}
                          />
                        }
                        label="I agree to the terms and conditions"
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setOrderStep(1)}>
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleConfirmOrder}
                          disabled={!agreeToTerms || creating}
                          startIcon={creating ? <CircularProgress size={20} /> : <CheckCircle />}
                        >
                          {creating ? 'Creating Order...' : 'Confirm Order'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
      </Dialog>

      {/* User Payment Methods Modal */}
      <UserPaymentMethodsModal
        open={showPaymentMethods}
        onClose={() => setShowPaymentMethods(false)}
        onSave={handlePaymentMethodsSaved}
      />
    </Box>
  );
};

export default P2PSellInterface;
